import React, { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Check, Copy } from "lucide-react";
import { usePoolMembers } from "../../hooks/usePoolMembers";
import { Button, StatusMessage, Textarea } from "../ui";
import { cn } from "../../lib/utils";

type Step3Props = {
  poolCode: string;
  onInvite: (emails: string[]) => Promise<void>;
};

export function Step3_Invite({ poolCode, onInvite }: Step3Props) {
  const { user } = useUser();
  const {
    members,
    isLoading,
    error,
    reload,
    approveMember,
    rejectMember,
    disableMember,
    enableMember,
  } = usePoolMembers(poolCode);
  const [emails, setEmails] = useState("");
  const [isInviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [isPoolCodeCopied, setIsPoolCodeCopied] = useState(false);
  const currentEmail = user?.primaryEmailAddress?.emailAddress?.toLowerCase();

  const filteredMembers =
    statusFilter === "ALL" ? members : members.filter((member) => member.status === statusFilter);
  const memberFilters = [
    { value: "ALL", label: "All" },
    { value: "PENDING", label: "Pending" },
    { value: "ACTIVE", label: "Active" },
  ];

  const handleCopyPoolCode = async () => {
    try {
      await navigator.clipboard.writeText(poolCode);
      setIsPoolCodeCopied(true);
      setTimeout(() => setIsPoolCodeCopied(false), 1800);
    } catch {
      setInviteError("Unable to copy the pool code.");
    }
  };

  const handleInvite = async () => {
    const emailList = emails.split(/\s*[,\s]+\s*/).filter(Boolean);
    if (emailList.length === 0) {
      setInviteError("Please enter at least one email address.");
      return;
    }

    setInviting(true);
    setInviteError(null);
    setSuccessMessage(null);

    try {
      await onInvite(emailList);
      setSuccessMessage(`Successfully invited ${emailList.length} player${emailList.length > 1 ? "s" : ""}.`);
      setEmails("");
      await reload();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "An unknown error occurred.");
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (memberEmail: string) => {
    setActionInProgress(memberEmail);
    setInviteError(null);
    setSuccessMessage(null);
    try {
      await onInvite([memberEmail]);
      setSuccessMessage(`Resent invite email to ${memberEmail}.`);
      await reload();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Failed to resend invite.");
    } finally {
      setActionInProgress(null);
    }
  };

  const handleApprove = async (memberEmail: string) => {
    setActionInProgress(memberEmail);
    setInviteError(null);
    setSuccessMessage(null);
    try {
      await approveMember(memberEmail);
      setSuccessMessage(`Approved ${memberEmail}.`);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Failed to approve member.");
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (memberEmail: string) => {
    setActionInProgress(memberEmail);
    setInviteError(null);
    setSuccessMessage(null);
    try {
      await rejectMember(memberEmail);
      setSuccessMessage(`Rejected ${memberEmail}.`);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Failed to reject member.");
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDisable = async (memberEmail: string) => {
    setActionInProgress(memberEmail);
    setInviteError(null);
    setSuccessMessage(null);
    try {
      await disableMember(memberEmail);
      setSuccessMessage(`Disabled ${memberEmail}.`);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Failed to disable member.");
    } finally {
      setActionInProgress(null);
    }
  };

  const handleEnable = async (memberEmail: string) => {
    setActionInProgress(memberEmail);
    setInviteError(null);
    setSuccessMessage(null);
    try {
      await enableMember(memberEmail);
      setSuccessMessage(`Enabled ${memberEmail}.`);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Failed to enable member.");
    } finally {
      setActionInProgress(null);
    }
  };

  const statusStyleMap: Record<string, string> = {
    ACTIVE: "border-success/50 bg-success-soft text-success",
    PENDING: "border-warning/50 bg-warning-soft text-warning",
    REJECTED: "border-danger/50 bg-danger-soft text-danger",
    DISABLED: "border-slate-600/60 bg-slate-800 text-slate-300",
  };

  return (
    <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
      <section className="rounded-lg border border-slate-800 bg-slate-900/70 px-4 py-4">
        <p className="font-display text-label-sm font-bold uppercase tracking-[0.14em] text-slate-400">
          Pool Code
        </p>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="min-w-0 truncate font-mono text-[2rem] font-semibold uppercase leading-none tracking-[0.12em] text-slate-100 md:text-[2.5rem]">
            {poolCode}
          </p>
          <button
            type="button"
            onClick={handleCopyPoolCode}
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-700 bg-slate-950/30 px-3 py-2 font-display text-sm font-bold text-brand transition hover:border-brand/60 hover:bg-brand/10"
          >
            {isPoolCodeCopied ? (
              <Check className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
            {isPoolCodeCopied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="mt-3 text-sm font-medium leading-relaxed text-slate-400">
          Share this code with players so they can join from Home.
        </p>
      </section>

      <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/70 px-4 py-4">
        <div className="space-y-2">
          <h2 className="font-display text-label-sm font-bold uppercase tracking-[0.14em] text-slate-400">
            Invite by Email
          </h2>
          <p className="text-sm font-medium leading-relaxed text-slate-300">
            Enter emails separated by commas or spaces. We&apos;ll invite new users or link existing accounts.
          </p>
        </div>
        <Textarea
          id="invite-emails"
          rows={2}
          placeholder="Enter email addresses"
          value={emails}
          onChange={(event) => setEmails(event.target.value)}
          disabled={isInviting}
          className="min-h-[72px]"
        />
        <div className="flex gap-2">
          <Button type="button" className="min-h-11 flex-1 text-base" onClick={handleInvite} disabled={isInviting}>
            {isInviting ? "Sending..." : "Send Invites"}
          </Button>
          <Button type="button" size="md" variant="secondary" onClick={() => setEmails("")} disabled={isInviting || !emails}>
            Clear
          </Button>
        </div>
        {inviteError && <StatusMessage variant="error">{inviteError}</StatusMessage>}
        {successMessage && <StatusMessage variant="success">{successMessage}</StatusMessage>}
      </section>

      <section className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-label-sm font-bold uppercase tracking-[0.14em] text-slate-400">
            Members · {filteredMembers.length}
          </h2>
          <div className="flex shrink-0 items-center gap-2" role="group" aria-label="Member status filter">
            {memberFilters.map((option) => {
              const isSelected = statusFilter === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatusFilter(option.value)}
                  className={cn(
                    "min-h-8 rounded-md border px-3 py-1 font-display text-sm font-semibold transition",
                    isSelected
                      ? "border-brand bg-brand/15 text-brand"
                      : "border-slate-700 bg-slate-950/20 text-slate-300 hover:border-slate-500 hover:text-slate-100",
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        {isLoading && <p className="text-xs text-slate-500">Loading...</p>}
        {error && <StatusMessage variant="error">{error}</StatusMessage>}
        {members.length === 0 && !isLoading && (
          <p className="text-xs text-slate-500">No members yet. Send invites above.</p>
        )}
        {members.length > 0 && (
          <div className="min-h-0 flex-1 overflow-auto">
            {/* Mobile: Card layout */}
            <div className="flex flex-col gap-2 md:hidden">
              {filteredMembers.map((member) => (
                <div key={member.email} className="flex items-center justify-between gap-2 border-b border-slate-800 pb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-xs text-slate-300">{member.email}</span>
                      <span
                        className={cn(
                          "shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold uppercase",
                          statusStyleMap[member.status] ?? "bg-slate-800 text-slate-400",
                        )}
                      >
                        {member.status}
                      </span>
                    </div>
                    <div className="flex gap-2 text-[10px] text-slate-500">
                      <span className="font-mono">{member.trader_code}</span>
                      {member.is_admin && <span>Admin</span>}
                    </div>
                  </div>
                  {member.status === "PENDING" ? (
                    <div className="flex shrink-0 gap-1">
                      {member.has_pending_invite ? (
                        <button
                          type="button"
                          onClick={() => handleResendInvite(member.email)}
                          disabled={actionInProgress === member.email}
                          className="rounded bg-slate-800 px-2 py-1 text-[10px] text-brand hover:bg-slate-700 disabled:opacity-50"
                        >
                          {actionInProgress === member.email ? "..." : "Resend"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleApprove(member.email)}
                        disabled={actionInProgress === member.email}
                        className="rounded bg-slate-700 px-2 py-1 text-[10px] text-slate-200 hover:bg-slate-600 disabled:opacity-50"
                      >
                        {actionInProgress === member.email ? "..." : "✓"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReject(member.email)}
                        disabled={actionInProgress === member.email}
                        className="rounded bg-slate-800 px-2 py-1 text-[10px] text-danger hover:bg-slate-700 disabled:opacity-50"
                      >
                        {actionInProgress === member.email ? "..." : "✗"}
                      </button>
                    </div>
                  ) : member.status === "DISABLED" ? (
                    <div className="flex shrink-0 gap-1">
                      {member.has_pending_invite ? (
                        <button
                          type="button"
                          onClick={() => handleResendInvite(member.email)}
                          disabled={actionInProgress === member.email}
                          className="rounded bg-slate-800 px-2 py-1 text-[10px] text-brand hover:bg-slate-700 disabled:opacity-50"
                        >
                          {actionInProgress === member.email ? "..." : "Resend"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleEnable(member.email)}
                        disabled={actionInProgress === member.email}
                        className="rounded bg-slate-700 px-2 py-1 text-[10px] text-slate-200 hover:bg-slate-600 disabled:opacity-50"
                      >
                        {actionInProgress === member.email ? "..." : "Enable"}
                      </button>
                    </div>
                  ) : member.status === "ACTIVE" &&
                    member.email.toLowerCase() !== currentEmail ? (
                    <div className="flex shrink-0 gap-1">
                      {member.has_pending_invite ? (
                        <button
                          type="button"
                          onClick={() => handleResendInvite(member.email)}
                          disabled={actionInProgress === member.email}
                          className="rounded bg-slate-800 px-2 py-1 text-[10px] text-brand hover:bg-slate-700 disabled:opacity-50"
                        >
                          {actionInProgress === member.email ? "..." : "Resend"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleDisable(member.email)}
                        disabled={actionInProgress === member.email}
                        className="rounded bg-slate-800 px-2 py-1 text-[10px] text-slate-200 hover:bg-slate-700 disabled:opacity-50"
                      >
                        {actionInProgress === member.email ? "..." : "Disable"}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            {/* Desktop: Table layout */}
            <div className="hidden max-h-[240px] overflow-auto rounded border border-slate-700 md:block">
              <table className="min-w-full divide-y divide-slate-700 text-xs">
                <thead className="sticky top-0 bg-slate-800 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium">Email</th>
                    <th className="px-2 py-2 text-left font-medium">Status</th>
                    <th className="px-2 py-2 text-left font-medium">Code</th>
                    <th className="px-2 py-2 text-left font-medium">Admin</th>
                    <th className="px-2 py-2 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 text-slate-300">
                  {filteredMembers.map((member) => (
                    <tr key={member.email} className="hover:bg-slate-800/50">
                      <td className="px-2 py-2">{member.email}</td>
                      <td className="px-2 py-2">
                        <span
                          className={cn(
                            "inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-semibold uppercase",
                            statusStyleMap[member.status] ?? "border-slate-700 bg-slate-800 text-slate-400",
                          )}
                        >
                          {member.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 font-mono text-xs text-slate-500">{member.trader_code}</td>
                      <td className="px-2 py-2">{member.is_admin ? "Yes" : "No"}</td>
                      <td className="px-2 py-2">
                        {member.status === "PENDING" ? (
                          <div className="flex gap-1">
                            {member.has_pending_invite ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResendInvite(member.email)}
                                disabled={actionInProgress === member.email}
                              >
                                {actionInProgress === member.email ? "..." : "Resend Invite"}
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => handleApprove(member.email)}
                              disabled={actionInProgress === member.email}
                            >
                              {actionInProgress === member.email ? "..." : "Approve"}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="border border-danger/50 text-danger hover:bg-danger-soft/20"
                              onClick={() => handleReject(member.email)}
                              disabled={actionInProgress === member.email}
                            >
                              {actionInProgress === member.email ? "..." : "Reject"}
                            </Button>
                          </div>
                        ) : member.status === "DISABLED" ? (
                          <div className="flex gap-1">
                            {member.has_pending_invite ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResendInvite(member.email)}
                                disabled={actionInProgress === member.email}
                              >
                                {actionInProgress === member.email ? "..." : "Resend Invite"}
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => handleEnable(member.email)}
                              disabled={actionInProgress === member.email}
                            >
                              {actionInProgress === member.email ? "..." : "Enable"}
                            </Button>
                          </div>
                        ) : member.status === "ACTIVE" &&
                          member.email.toLowerCase() !== currentEmail ? (
                          <div className="flex gap-1">
                            {member.has_pending_invite ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResendInvite(member.email)}
                                disabled={actionInProgress === member.email}
                              >
                                {actionInProgress === member.email ? "..." : "Resend Invite"}
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="border border-danger/50 text-danger hover:bg-danger-soft/20"
                              onClick={() => handleDisable(member.email)}
                              disabled={actionInProgress === member.email}
                            >
                              {actionInProgress === member.email ? "..." : "Disable"}
                            </Button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
